import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, FolderKanban, ShieldCheck, Camera, FileText, Users, LogOut, Plus, BarChart3, FileStack, ShieldAlert, Map as MapIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import SyncStatusBar from "@/components/SyncStatusBar";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, key: "nav-dashboard" },
  { to: "/projects", label: "Projects", icon: FolderKanban, key: "nav-projects" },
  { to: "/map", label: "Map", icon: MapIcon, key: "nav-map" },
  { to: "/incidents", label: "Incidents", icon: ShieldAlert, key: "nav-incidents" },
  { to: "/reports", label: "Reports", icon: BarChart3, key: "nav-reports", roles: ["admin", "pm", "foreman"] },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex">
      {/* Sidebar (desktop) */}
      <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-slate-800 bg-slate-950">
        <div className="p-5 border-b border-slate-800">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
            <img src="/force2-logo.png" alt="Force2 Telecom" className="w-10 h-10 object-contain rounded-md bg-slate-900 border border-slate-800" />
            <div className="leading-tight">
              <div className="font-display font-black tracking-tight text-slate-50">FORCE2</div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-slate-400">FieldOps</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.filter((n) => !n.roles || n.roles.includes(user?.role)).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={n.key}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-blue-900/30 text-blue-300 border border-blue-900/60"
                    : "text-slate-300 hover:bg-slate-900 hover:text-slate-50 border border-transparent"
                }`
              }
            >
              <n.icon className="w-4 h-4" /> {n.label}
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <>
              <NavLink
                to="/admin/templates"
                data-testid="nav-admin-templates"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-blue-900/30 text-blue-300 border border-blue-900/60"
                      : "text-slate-300 hover:bg-slate-900 hover:text-slate-50 border border-transparent"
                  }`
                }
              >
                <FileStack className="w-4 h-4" /> Templates
              </NavLink>
              <NavLink
                to="/admin"
                end
                data-testid="nav-admin"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-blue-900/30 text-blue-300 border border-blue-900/60"
                      : "text-slate-300 hover:bg-slate-900 hover:text-slate-50 border border-transparent"
                  }`
                }
              >
                <Users className="w-4 h-4" /> Users
              </NavLink>
            </>
          )}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <Button
            className="w-full bg-red-700 hover:bg-red-600 font-display tracking-wide"
            onClick={() => navigate("/projects/new")}
            data-testid="sidebar-new-project"
          >
            <Plus className="w-4 h-4 mr-1.5" /> NEW PROJECT
          </Button>
          <div className="mt-3 px-1 text-xs text-slate-400">
            <Link to="/profile" className="block hover:bg-slate-800/50 -mx-1 px-1 py-1 rounded transition-colors" data-testid="sidebar-profile-link">
              <div className="truncate font-medium text-slate-200" data-testid="sidebar-user-name">{user?.full_name}</div>
              <div className="truncate uppercase tracking-wider text-[10px] text-slate-500">{user?.role} · view profile</div>
            </Link>
          </div>
          <button
            onClick={() => { logout(); navigate("/login"); }}
            className="mt-2 w-full flex items-center justify-center gap-2 text-xs text-slate-400 hover:text-red-400 py-2 border border-slate-800 rounded-md"
            data-testid="sidebar-logout"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar (mobile only) */}
        <header className="md:hidden flex items-center justify-between p-3 border-b border-slate-800 bg-slate-950">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link-mobile">
            <img src="/force2-logo.png" alt="Force2 Telecom" className="w-9 h-9 object-contain rounded-md bg-slate-900 border border-slate-800" />
            <div className="font-display font-black tracking-tight">FORCE2</div>
          </Link>
          <button
            onClick={() => { logout(); navigate("/login"); }}
            className="text-xs text-slate-400 px-2 py-1.5 border border-slate-800 rounded"
            data-testid="topbar-logout"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </header>

        <div className="brand-bar" />
        <SyncStatusBar />

        <main className="flex-1 p-4 md:p-8 pb-24 md:pb-8 max-w-[1400px] w-full mx-auto">{children}</main>

        {/* Mobile bottom nav */}
        <nav className="md:hidden fixed bottom-0 inset-x-0 bg-slate-950 border-t border-slate-800 flex z-40">
          <NavLink to="/" end className={({isActive})=>`flex-1 flex flex-col items-center py-2.5 text-[11px] ${isActive?"text-blue-400":"text-slate-400"}`} data-testid="bottomnav-dashboard">
            <LayoutDashboard className="w-5 h-5 mb-0.5"/>Dash
          </NavLink>
          <NavLink to="/projects" className={({isActive})=>`flex-1 flex flex-col items-center py-2.5 text-[11px] ${isActive?"text-blue-400":"text-slate-400"}`} data-testid="bottomnav-projects">
            <FolderKanban className="w-5 h-5 mb-0.5"/>Projects
          </NavLink>
          <NavLink to="/map" className={({isActive})=>`flex-1 flex flex-col items-center py-2.5 text-[11px] ${isActive?"text-blue-400":"text-slate-400"}`} data-testid="bottomnav-map">
            <MapIcon className="w-5 h-5 mb-0.5"/>Map
          </NavLink>
          <NavLink to="/projects/new" className="flex-1 flex flex-col items-center py-2.5 text-[11px] text-slate-400" data-testid="bottomnav-new">
            <Plus className="w-5 h-5 mb-0.5"/>New
          </NavLink>
          {user?.role === "admin" && (
            <NavLink to="/admin" end className={({isActive})=>`flex-1 flex flex-col items-center py-2.5 text-[11px] ${isActive?"text-blue-400":"text-slate-400"}`} data-testid="bottomnav-admin">
              <Users className="w-5 h-5 mb-0.5"/>Admin
            </NavLink>
          )}
        </nav>
      </div>
    </div>
  );
}
